import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Loader2, Sparkles, Phone } from 'lucide-react';
import { publicApi } from '../lib/api';
import { contactInfo } from '../data/mock';

const SESSION_KEY = 'bls_chat_session';

const genSession = () => 'sess_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = genSession();
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

const initialAssistant = {
  role: 'assistant',
  text: "Hi love, I'm Bliss \u2728 I can help you pick the right treatment or answer any questions about Dawn's mobile spa. What are you in the mood for?",
};

const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([initialAssistant]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const session = getSession();

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Load prior history on first open
  useEffect(() => {
    if (!open) return;
    publicApi.get('/chat/history', { params: { session_id: session } }).then((r) => {
      if (r.data && r.data.length) {
        setMessages(r.data.map((m) => ({ role: m.role, text: m.text })));
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setInput('');
    setSending(true);
    try {
      const r = await publicApi.post('/chat/concierge', { session_id: session, message: text });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'assistant',
        text: "I'm having a little trouble reaching my brain right now. Please WhatsApp Dawn directly and she'll help you personally.",
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const bookOnWhatsApp = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const preface = lastUser
      ? `Hi Dawn! I chatted with Bliss on your site about "${lastUser.text}". `
      : "Hi Dawn! I'd love to book a treatment. ";
    const msg = encodeURIComponent(preface + 'Could you confirm your availability and any next steps? Thank you!');
    window.open(`https://wa.me/${contactInfo.whatsappIntl}?text=${msg}`, '_blank');
  };

  const reset = () => {
    const newSession = genSession();
    localStorage.setItem(SESSION_KEY, newSession);
    setMessages([initialAssistant]);
    // reload once for the useEffect to pick up new session on next open
    window.location.hash = window.location.hash;
  };

  return (
    <>
      {/* Floating open button */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Chat with concierge"
        className="fixed bottom-24 right-6 z-50 inline-flex items-center justify-center w-14 h-14 rounded-full text-white shadow-2xl hover:scale-110 transition-transform duration-300"
        style={{ background: 'linear-gradient(135deg,#e9a0aa,#b8a4d8)' }}
      >
        {open ? <X size={22} /> : <MessageSquare size={22} strokeWidth={1.8} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-44 right-4 sm:right-6 z-50 w-[92vw] sm:w-[380px] max-w-md h-[70vh] sm:h-[560px] bg-white/85 backdrop-blur-lg border border-white/60 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between text-white" style={{ background: 'linear-gradient(135deg,#e9a0aa,#b8a4d8)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center">
                  <Sparkles size={16} />
                </div>
                <div>
                  <p className="tracking-[0.25em] text-[10px] opacity-90" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>BLISSFULL MOBILE SPA</p>
                  <p className="font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem', lineHeight: 1.1 }}>Chat with Bliss</p>
                </div>
              </div>
              <button onClick={reset} className="text-[10px] tracking-[0.2em] opacity-90 hover:opacity-100 underline underline-offset-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                NEW CHAT
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-[15px] leading-relaxed ${
                      m.role === 'user'
                        ? 'bg-neutral-900 text-white rounded-br-md'
                        : 'bg-white text-neutral-800 border border-neutral-200 rounded-bl-md'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-md px-4 py-3">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Book on WhatsApp handoff */}
            <div className="px-4 pt-2">
              <button
                onClick={bookOnWhatsApp}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-white text-xs tracking-[0.25em] bg-[#25D366] hover:brightness-110 transition-all"
                style={{ fontFamily: "'Josefin Sans', sans-serif" }}
              >
                <Phone size={12} /> BOOK ON WHATSAPP
              </button>
            </div>

            {/* Input */}
            <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-neutral-200 bg-white/70">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about treatments, prices, timing..."
                className="flex-1 bg-transparent outline-none px-3 py-2 text-neutral-800 placeholder-neutral-400 text-sm"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="p-2.5 rounded-full text-white disabled:opacity-40 transition-all"
                style={{ background: 'linear-gradient(135deg,#e9a0aa,#b8a4d8)' }}
                aria-label="Send"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConciergeChat;
